import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { StaffUser, StaffAuthState } from '../types';
import { statusApi } from '../services/api';

interface StaffAuthContextType extends StaffAuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const StaffAuthContext = createContext<StaffAuthContextType | undefined>(undefined);

export const useStaffAuth = () => {
  const context = useContext(StaffAuthContext);
  if (!context) {
    throw new Error('useStaffAuth must be used within a StaffAuthProvider');
  }
  return context;
};

interface StaffAuthProviderProps {
  children: ReactNode;
}

export const StaffAuthProvider: React.FC<StaffAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<StaffUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    try {
      const storedUser = statusApi.getStaffUser();
      const token = statusApi.getStaffToken();

      if (storedUser && token) {
        // Verify token is still valid
        const profile = await statusApi.getStaffProfile();
        setUser(profile);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch {
      // Token invalid, clear auth
      setUser(null);
      setIsAuthenticated(false);
      statusApi.setStaffToken('');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { user: staffUser, token } = await statusApi.staffLogin(email, password);
    statusApi.setStaffToken(token);
    statusApi.setStaffUser(staffUser);
    setUser(staffUser);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    await statusApi.staffLogout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: StaffAuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
  };

  return (
    <StaffAuthContext.Provider value={value}>
      {children}
    </StaffAuthContext.Provider>
  );
};

export default StaffAuthContext;
